import { useState, useEffect } from "react";
import { Clock, Phone, Menu, X } from "lucide-react";
import { business } from "../../config/business";
import { trackEvent } from "../../lib/gtag";
import Button from "../ui/Button";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-us" },
  { label: "Service Areas", href: "#service-areas" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "shadow-lg bg-navy-800/95 backdrop-blur-md" : "bg-navy-800"
      }`}
    >
      {/* Top accent bar */}
      <div className="bg-navy-900 py-1.5 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-success-500 font-medium">
            <Clock size={13} />
            <span>Open 24 Hours</span>
          </div>
          <a
            href={`tel:${business.phone}`}
            onClick={() => trackEvent('phone_call_click', { method: 'tel_link' })}
            className="flex items-center gap-1.5 text-ink-300 hover:text-white transition-colors duration-200"
          >
            <Phone size={13} />
            <span>{business.phoneDisplay}</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <a
          href="/"
          className="flex items-center gap-3 leading-none group"
          aria-label="Fateh Heating and Cooling — Home"
        >
          <img src="/logo-v1.svg" alt="" className="h-10 w-auto" aria-hidden="true" />
          <span className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-white text-xl tracking-tight group-hover:text-accent-400 transition-colors duration-200">
              Fateh
            </span>
            <span className="font-heading font-medium text-accent-400 text-[10px] tracking-widest uppercase">
              Heating &amp; Cooling
            </span>
          </span>
        </a>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-ink-300 hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop right-side actions */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href={`tel:${business.phone}`}
            onClick={() => trackEvent('phone_call_click', { method: 'tel_link' })}
            className="flex items-center gap-1.5 text-ink-300 hover:text-white transition-colors duration-200 text-sm font-medium"
          >
            <Phone size={14} />
            {business.phoneDisplay}
          </a>
          <Button variant="primary" size="sm" as="a" href="#contact">
            Get a Free Quote
          </Button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-white p-2 rounded-md hover:bg-navy-700 transition-colors duration-200"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile slide-out drawer */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-navy-900 border-t border-navy-700 px-4 py-6 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-ink-300 hover:text-white transition-colors duration-200 text-base font-medium py-3 border-b border-navy-800 last:border-0"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <div className="pt-4 flex flex-col gap-3">
            <a
              href={`tel:${business.phone}`}
              onClick={() => trackEvent('phone_call_click', { method: 'tel_link' })}
              className="flex items-center gap-2 text-white font-semibold text-base"
            >
              <Phone size={16} className="text-accent-400" />
              {business.phoneDisplay}
            </a>
            <Button
              variant="primary"
              as="a"
              href="#contact"
              className="w-full text-center"
              onClick={() => setMenuOpen(false)}
            >
              Get a Free Quote
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
